import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Loader2, PlayCircle, AlertTriangle, Users, Building2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AppUnavailable from "@/pages/AppUnavailable";

const ROLES = [
  { value: "customer", label: "Customer / partner user" },
  { value: "staff", label: "Staff" },
  { value: "super_admin", label: "Super admin" },
];
const PARTNER_TYPES = ["", "agent", "reseller", "master_agent", "service_provider", "referral"];

export default function KBVisibility() {
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [matrix, setMatrix] = useState(null);
  const [options, setOptions] = useState({ teams: [], organizations: [] });
  const [sim, setSim] = useState({ role: "customer", partner_type: "", team: "", org: "" });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/kb/access/matrix").then((r) => setMatrix(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); });
    api.get("/kb/visibility/options")
      .then((r) => setOptions({ teams: r.data.teams || [], organizations: r.data.organizations || [] }))
      .catch(() => {});
  }, []);

  const runPreview = async () => {
    setRunning(true);
    try {
      const params = { role: sim.role, partner_type: sim.partner_type, team: sim.team, org: sim.org };
      const { data } = await api.get("/kb/access/preview", { params });
      setResult(data);
    } finally { setRunning(false); }
  };

  if (denied) return <AppUnavailable appName="KB" inactive={false} />;

  return (
    <div className="mx-auto max-w-5xl" data-testid="kb-visibility-page">
      <button onClick={() => navigate("/kb")} data-testid="kb-visibility-back"
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
        <ArrowLeft className="h-4 w-4" /> Back to Knowledge Base
      </button>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Content Visibility</h1>
        </div>
        <Button variant="outline" size="sm" data-testid="kb-visibility-bulk-link"
          onClick={() => navigate("/kb/bulk-visibility")}>Bulk edit visibility →</Button>
      </div>

      {/* Access simulator */}
      <section className="mb-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-access-simulator">
        <h2 className="mb-1 font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">Preview as…</h2>
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">See exactly what a given viewer can access. Staff see every audience, but team and organization narrowing applies to them too — only super admins see everything.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Role</Label>
            <Select value={sim.role} onValueChange={(v) => setSim({ ...sim, role: v })}>
              <SelectTrigger data-testid="kb-sim-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Partner type</Label>
            <Select value={sim.partner_type || "none"} onValueChange={(v) => setSim({ ...sim, partner_type: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="kb-sim-partner"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— none —</SelectItem>
                {PARTNER_TYPES.filter(Boolean).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Team</Label>
            <Select value={sim.team || "none"} onValueChange={(v) => setSim({ ...sim, team: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="kb-sim-team"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— any —</SelectItem>
                {options.teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Organization</Label>
            <Select value={sim.org || "none"} onValueChange={(v) => setSim({ ...sim, org: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="kb-sim-org"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="none">— any —</SelectItem>
                {options.organizations.slice(0, 300).map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={runPreview} disabled={running} data-testid="kb-sim-run"
          className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90">
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />} Preview access
        </Button>

        {result && (
          <div className="mt-5 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 p-4" data-testid="kb-sim-result">
            <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-slate-800 dark:text-slate-100">{result.viewer.label}</span>
              {result.viewer.unrestricted
                ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid="kb-sim-unrestricted">sees everything</span>
                : result.viewer.is_staff && <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" data-testid="kb-sim-staff">staff · narrowed by team</span>}
              <span className="text-slate-400 dark:text-slate-500">audiences: {result.viewer.audiences.join(", ")}</span>
              {result.viewer.teams?.length > 0 && (
                <span className="text-slate-400 dark:text-slate-500" data-testid="kb-sim-teams">teams: {result.viewer.teams.join(", ")}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-white dark:bg-slate-900 p-3 text-center shadow-sm" data-testid="kb-sim-articles">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{result.articles.visible.toLocaleString()}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">of {result.articles.total_published.toLocaleString()} published articles</div>
              </div>
              <div className="rounded-lg bg-white dark:bg-slate-900 p-3 text-center shadow-sm" data-testid="kb-sim-categories">
                <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{result.categories.visible.toLocaleString()}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">of {result.categories.total.toLocaleString()} categories</div>
              </div>
            </div>
            {result.sample_categories?.length > 0 && (
              <div className="mt-3">
                <div className="mb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">Top-level categories they'd see</div>
                <div className="flex flex-wrap gap-1.5">
                  {result.sample_categories.map((c) => (
                    <span key={c.slug} className="rounded-full bg-white dark:bg-slate-900 px-2.5 py-1 text-xs text-slate-600 dark:text-slate-300 shadow-sm">{c.name.trim()}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Who-sees-what matrix */}
      <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-matrix">
        <h2 className="mb-4 font-heading text-lg font-semibold text-slate-800 dark:text-slate-100">Who sees what</h2>
        {!matrix ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-slate-100 dark:border-slate-800">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/60 text-left text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr><th className="px-4 py-2">Audience</th><th className="px-4 py-2 text-right">Published articles</th><th className="px-4 py-2 text-right">Categories</th></tr>
                </thead>
                <tbody>
                  {matrix.matrix.map((r) => (
                    <tr key={r.audience} className="border-t border-slate-100 dark:border-slate-800" data-testid={`kb-matrix-${r.audience}`}>
                      <td className="px-4 py-2 font-medium text-slate-700 dark:text-slate-200">{r.label}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{r.articles.toLocaleString()}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400">{r.categories.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="kb-matrix-flags">
              <Flag icon={AlertTriangle} tone={matrix.flags.no_audience ? "amber" : "slate"}
                label="No audience (staff-only)" value={matrix.flags.no_audience} />
              <Flag icon={AlertTriangle} tone={matrix.flags.everyone ? "rose" : "slate"}
                label="Visible to everyone" value={matrix.flags.everyone} />
              <Flag icon={Users} tone="slate" label="Team-targeted" value={matrix.flags.team_targeted} />
              <Flag icon={Building2} tone="slate" label="Org-targeted" value={matrix.flags.org_targeted} />
              <Flag icon={ShieldCheck} tone="slate" label="Drafts (hidden)" value={matrix.flags.drafts} />
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function Flag({ icon: Icon, label, value, tone }) {
  const tones = {
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    slate: "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300",
  };
  return (
    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tones[tone] || tones.slate}`}>
      <Icon className="h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <div className="text-base font-bold tabular-nums">{(value ?? 0).toLocaleString()}</div>
        <div className="truncate text-[11px]">{label}</div>
      </div>
    </div>
  );
}
